import { AxiosError, isAxiosError } from 'axios';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { alimtalkSendHistoryApi } from '../../../../../lib/api/alimtalk/alimtalkSendHistoryApi';
import toastMsg from '../../../../../lib/toastMsg';
import { ErrorResponseType, extractErrorMessage } from '../../../../../lib/utils/checkErrorResponse';
import {
  AlimtalkSendCancelResponse,
  AlimtalkSendHistoryDetailResponse,
  AlimtalkSendHistoryListQueryParams,
  AlimtalkSendHistoryListResponse,
} from '../types';
import { resolveAutoRefreshInterval } from '../utils';

export const ALIMTALK_SEND_HISTORY_QUERY_KEY = {
  all: ['alimtalkSendHistory'] as const,
  list: (params: AlimtalkSendHistoryListQueryParams) => ['alimtalkSendHistory', 'list', params] as const,
  detail: (id: number) => ['alimtalkSendHistory', 'detail', id] as const,
};

const DEFAULT_QUERY_ERROR_MESSAGE = '잠시 후 다시 시도해 주세요.';

/** 화면 분기(404 문구 등)에 필요한 HTTP status·백엔드 에러 코드를 보존한 Error */
export type AlimtalkSendHistoryScreenError = Error & { status?: number; code?: string };

/**
 *  조회 실패를 화면 에러(Result) 하나로만 알리기 위해 AxiosError 를 일반 Error 로 바꿔 던진다.
 *  - root.tsx 전역 QueryCache.onError 는 AxiosError 일 때만 토스트를 띄우므로, 일반 Error 는 토스트 대상에서 빠진다.
 *    (전역 핸들러에는 쿼리 단위로 토스트를 끄는 meta 옵션이 없어, 전역 동작은 그대로 두고 이 쿼리에서만 우회한다)
 *  - 인터셉터 모달은 API 쪽 skipErrorModal 로 끈다. 만료 토큰·비활성 관리자 모달은 그대로 동작한다.
 *  - message 에는 백엔드 errors[0].message 를 담아 화면에서 그대로 보여준다.
 */
const toScreenOnlyError = (error: unknown): AlimtalkSendHistoryScreenError => {
  if (!isAxiosError(error)) {
    return error instanceof Error ? error : new Error(DEFAULT_QUERY_ERROR_MESSAGE);
  }
  const { response } = error as AxiosError<{ errors?: { code?: string; message?: string }[] }>;
  const backendError = response?.data?.errors?.[0];
  return Object.assign(new Error(backendError?.message || DEFAULT_QUERY_ERROR_MESSAGE), {
    status: response?.status,
    code: backendError?.code,
  });
};

/** 권한 없음(403) — 다시 시도해도 같은 결과라 화면에서 재시도 버튼 없이 별도 문구로 보여준다 */
export const isForbiddenScreenError = (error: AlimtalkSendHistoryScreenError | null | undefined): boolean =>
  error?.status === 403;

const withScreenOnlyError = <T>(request: Promise<T>): Promise<T> =>
  request.catch((error: unknown) => {
    throw toScreenOnlyError(error);
  });

/**
 *  자동 갱신 공통 옵션.
 *  - 진행 중(PENDING/RUNNING/CANCELLING) 상태가 있을 때만 주기 갱신한다 — 주기는 refetchInterval 함수가 정한다.
 *  - 백그라운드 탭에서는 멈춘다(refetchIntervalInBackground: false). 탭으로 돌아오면 기본 refetchOnWindowFocus 가 갱신한다.
 *  - 재조회가 한 번 실패해도 멈추지 않는다 — 주기 판정은 이 쿼리의 마지막 성공 데이터(query.state.data) 기준이다.
 *    (v4 는 데이터가 있어도 재조회 실패 시 status 가 'error' 가 되므로 status 로 판정하면 한 번 실패로 갱신이 끊긴다.
 *     observer data 는 keepPreviousData 로 이전 조건 데이터일 수 있어 쓰지 않는다)
 *  - 권한 없음(403)이면 멈춘다 — 다시 불러도 같은 결과이고 화면도 403 Result 로 바뀐다.
 *  - 응답이 없거나(네트워크·타임아웃) 5xx 인 실패만 1회 재시도한다 (전역 retry: 0 은 그대로, 이 쿼리만).
 *    4xx 는 재시도해도 같은 결과라 바로 실패로 둔다.
 *  - 자동 갱신 중에는 로딩을 띄우지 않는다: 화면은 첫 로드(isLoading)·조건 변경·직접 재조회에만 로딩을 보여준다.
 */
const AUTO_REFRESH_OPTIONS = {
  refetchIntervalInBackground: false,
  retry: (failureCount: number, error: AlimtalkSendHistoryScreenError) =>
    failureCount < 1 && (error.status === undefined || error.status >= 500),
} as const;

/** 발송 내역 목록 조회 — 목록에 진행 중인 건이 하나라도 있으면 자동 갱신 */
export const useAlimtalkSendHistoryListQuery = (
  params: AlimtalkSendHistoryListQueryParams,
): UseQueryResult<AlimtalkSendHistoryListResponse, AlimtalkSendHistoryScreenError> =>
  useQuery(
    ALIMTALK_SEND_HISTORY_QUERY_KEY.list(params),
    () => withScreenOnlyError(alimtalkSendHistoryApi.getList(params)),
    {
      keepPreviousData: true,
      ...AUTO_REFRESH_OPTIONS,
      refetchInterval: (_data, query) =>
        isForbiddenScreenError(query.state.error)
          ? false
          : resolveAutoRefreshInterval(query.state.data?.sends.map(({ status }) => status)),
    },
  );

/** 발송 내역 상세 조회 — 해당 예약이 진행 중 상태일 때만 자동 갱신 */
export const useAlimtalkSendHistoryDetailQuery = (
  id: number,
): UseQueryResult<AlimtalkSendHistoryDetailResponse, AlimtalkSendHistoryScreenError> =>
  useQuery(
    ALIMTALK_SEND_HISTORY_QUERY_KEY.detail(id),
    () => withScreenOnlyError(alimtalkSendHistoryApi.getDetail(id)),
    {
      enabled: Number.isSafeInteger(id) && id > 0,
      ...AUTO_REFRESH_OPTIONS,
      refetchInterval: (_data, query) =>
        isForbiddenScreenError(query.state.error) || !query.state.data
          ? false
          : resolveAutoRefreshInterval([query.state.data.send.status]),
    },
  );

/**
 *  예약 취소 (PENDING / RUNNING)
 *  실패 알림은 이 onError 토스트 하나만 쓴다 — 인터셉터 모달은 API 쪽 skipErrorModal 로 끈다.
 *  (뮤테이션 에러는 전역 QueryCache.onError 범위 밖이라 전역 토스트와는 겹치지 않는다)
 */
export const useCancelAlimtalkSendMutation = (): UseMutationResult<AlimtalkSendCancelResponse, AxiosError, number> => {
  const queryClient = useQueryClient();

  return useMutation((id: number) => alimtalkSendHistoryApi.cancel(id), {
    onSuccess: () => {
      toastMsg.success('예약 취소 요청이 접수되었습니다.');
    },
    onError: (error: AxiosError) => {
      // 응답이 없는 실패(네트워크·타임아웃)는 axios 영어 원문 대신 기본 안내 문구를 보여준다
      const message = error.response
        ? extractErrorMessage(error as ErrorResponseType, '예약 취소에 실패했습니다.')
        : DEFAULT_QUERY_ERROR_MESSAGE;
      toastMsg.error(message, error);
    },
    /**
     *  실패해도 서버 상태(이미 취소·발송 시작 등)가 바뀌었을 수 있어 목록·상세를 항상 다시 불러온다.
     *  Promise 를 반환해 mutateAsync 가 재조회 완료까지 기다리게 한다 — 그동안 isLoading 이 유지돼
     *  확인 버튼 로딩·취소 버튼 비활성이 이어지고, 옛 PENDING 응답의 취소 버튼으로 중복 취소하지 못한다.
     *  (all 키 하나로 화면에 떠 있는 목록·상세 쿼리를 함께 무효화한다. 재조회 실패는 reject 하지 않는다)
     */
    onSettled: () => queryClient.invalidateQueries(ALIMTALK_SEND_HISTORY_QUERY_KEY.all),
  });
};
